import { useState } from 'react';
import KitCard from './KitCard';

export default function ProductSelector({
  products, kits, campaignPrices, selectedItems, onItemsChange, isAlternatives = false
}) {
  const [selectedKitId, setSelectedKitId] = useState(null);

  const priceMap = {};
  if (campaignPrices) {
    campaignPrices.forEach(cp => { priceMap[cp.product_id] = cp.discount_price; });
  }

  const handleKitSelect = (kit) => {
    if (selectedKitId === kit.id) {
      setSelectedKitId(null);
      onItemsChange([]);
      return;
    }
    setSelectedKitId(kit.id);
    const kitItems = (kit.products || []).map(kp => ({
      product_id: kp.product_id,
      name: kp.name,
      subcategory: kp.subcategory,
      quantity: kp.quantity || 1,
      normal_price: kp.normal_price,
      discount_price: priceMap[kp.product_id] || null,
      override_price: null,
    }));
    onItemsChange(kitItems);
  };

  const handleAddProduct = (product) => {
    const exists = selectedItems.find(i => i.product_id === product.id);
    if (exists) return;
    onItemsChange([...selectedItems, {
      product_id: product.id,
      name: product.name,
      subcategory: product.subcategory,
      quantity: 1,
      normal_price: product.normal_price,
      discount_price: priceMap[product.id] || null,
      override_price: null,
    }]);
  };

  const handleRemoveItem = (index) => {
    const next = [...selectedItems];
    next.splice(index, 1);
    onItemsChange(next);
    if (next.length === 0) setSelectedKitId(null);
  };

  const handleQuantityChange = (index, qty) => {
    const next = [...selectedItems];
    next[index] = { ...next[index], quantity: Math.max(1, parseInt(qty) || 1) };
    onItemsChange(next);
  };

  const handleOverridePrice = (index, price) => {
    const next = [...selectedItems];
    const val = price === '' ? null : parseInt(price) || null;
    next[index] = { ...next[index], override_price: val };
    onItemsChange(next);
  };

  const getEffectivePrice = (item) => {
    if (item.override_price != null) return item.override_price;
    if (item.discount_price != null) return item.discount_price;
    return item.normal_price;
  };

  const formatPrice = (n) => {
    if (n == null) return '';
    return '$' + n.toLocaleString('es-CL');
  };

  return (
    <div className="space-y-4">
      {/* Kit shortcuts */}
      {!isAlternatives && kits && kits.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Kits predefinidos</label>
          <div className="grid grid-cols-3 gap-2">
            {kits.map(kit => (
              <KitCard
                key={kit.id}
                kit={kit}
                isSelected={selectedKitId === kit.id}
                onSelect={handleKitSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add individual product */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isAlternatives ? 'Agregar alternativa' : '+ Agregar producto individual'}
        </label>
        <select
          onChange={(e) => {
            const p = products.find(x => x.id === parseInt(e.target.value));
            if (p) handleAddProduct(p);
            e.target.value = '';
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          defaultValue=""
        >
          <option value="" disabled>Seleccionar producto...</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name} - {formatPrice(p.normal_price)}</option>
          ))}
        </select>
      </div>

      {/* Selected items table */}
      {selectedItems.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Producto</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 w-16">Cant</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Precio</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 w-28">Override</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{item.name}</div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(idx, e.target.value)}
                      className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {item.discount_price != null && item.discount_price !== item.normal_price ? (
                      <div>
                        <span className="line-through text-gray-400 text-xs">{formatPrice(item.normal_price)}</span>
                        <br />
                        <span className="text-green-600 font-medium">{formatPrice(item.discount_price)}</span>
                      </div>
                    ) : (
                      <span>{formatPrice(item.normal_price)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      placeholder="Manual"
                      value={item.override_price ?? ''}
                      onChange={(e) => handleOverridePrice(idx, e.target.value)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => handleRemoveItem(idx)}
                      className="text-red-400 hover:text-red-600"
                      title="Eliminar"
                    >
                      x
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
